import React from 'react';
import {
  useAppSelector,
  useComparisonTimePeriodShortDisplayName,
  useMainTimePeriodShortDisplayName
} from 'src/utils/Hooks';
import {
  removeDspClicks,
  useCreateRemoveRetailPriceRangeFilters
} from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBaseMetricRequestBuilder';
import { CommonSummaryInfoSubtitle } from 'src/components/EntityPage/CommonSummaryInfo/CommonSummaryInfo';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { BEACON_CHART_SPACING } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import KeywordsWithTrafficSplineChart from '../KeywordsWithTrafficSplineChart';
import TotalTrafficGrid from '../TotalTrafficGrid';
import { TotalTrafficTopSection } from '../TotalTrafficTopSection';
import BeaconComparableSplineChart from 'src/components/BeaconV2Components/BeaconMetricSplineChart/BeaconComparableSplineChart';
import { TrafficByTopicBarChart } from '../../TrafficByTopicBarChart';
import { useMainEntityType } from 'src/utils/Hooks/reduxSelectors';
import { TrafficAdvancedSearchBarChart } from '../../TrafficAdvancedSearchBarChart';
import { onDisplayAdvertisingMode } from 'src/store/modules/user/selectors';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import _upperFirst from 'lodash/upperFirst';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import NoDataPlaceHolder, {
  NoDataIcon
} from 'src/components/BeaconV2Components/common/NoDataPlaceHolder/NoDataPlaceHolder';
import { NewBeaconFileIcon } from 'src/components/SvgIcons';
import { useTrafficAvailability } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Total/hooks/useTrafficAvailability';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';
import { ExportConfigProvider } from 'src/components/BeaconV2Components/common/AppExportModal/ExportModalContext';
import TrafficByKeywordWithDPV from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/TrafficByKeywordWithDPV';
import { ChartContextProvider } from 'src/utils/chartContext/ChartContextGroupContext';
import { CHART_CONTEXT_GROUPS, CHART_NAMES } from 'src/utils/chartContext/chartContextRegistry';

export const TrafficNoDataPlaceholder = () => {
  return (
    <Flex marginTop="111px" alignItems="center" justifyContent="center">
      <NoDataPlaceHolder
        title="Data currently unavailable"
        description="There is currently no data matching your selected criteria. Please try another query to view insights."
        icon={<NoDataIcon icon={<NewBeaconFileIcon />} />}
      />
    </Flex>
  );
};

/**
 * Total traffic page for non-topic or topic group entities
 */
export function NonTopicOrTopicGroupPage() {
  const mainEntityType = useMainEntityType();
  const removeRetailPriceRangeFilters = useCreateRemoveRetailPriceRangeFilters();
  const isProductEntity = mainEntityType === 'product';
  const isCategoryEntity = mainEntityType === 'category';
  const isSubcategoryEntity = mainEntityType === 'subcategory';
  const isBusinessEntity = mainEntityType === 'businessunit';
  const titleOnly = !(isProductEntity || isCategoryEntity || isSubcategoryEntity || isBusinessEntity);

  const mainPeriodDisplayName = useMainTimePeriodShortDisplayName();
  const comparisonDisplayName = useComparisonTimePeriodShortDisplayName();
  const entityPhrase = useEntityPhrasing({ titleOnly });
  const taxonomyText = useTaxonomySubtitleText();
  const isAdvertisingDisplayMode = useAppSelector(onDisplayAdvertisingMode);

  const index = mainEntityType === 'searchtermlist' || !isAdvertisingDisplayMode ? 'traffic' : 'aggregated-traffic';

  const { dataAvailable } = useTrafficAvailability();

  const getSubtitle = ({ totalTraffic, formattedPercent, direction }) => {
    const changeDirection = direction < 0 ? 'lower' : 'higher';
    return (
      <BeaconSubtitleContainer>
        <Text variant="body1">
          {`${_upperFirst(entityPhrase)} earned ${totalTraffic} total traffic `}
          {taxonomyText}
          {` during the ${mainPeriodDisplayName} time period, which is ${formattedPercent} ${changeDirection} compared to the ${comparisonDisplayName}.`}
          <SubtitleFilters />
        </Text>
      </BeaconSubtitleContainer>
    );
  };

  return (
    <ExportConfigProvider overrideShouldIntercept={true}>
      <BasePageContainer>
        {dataAvailable ? (
          <Flex flexDirection="column" gap={BEACON_CHART_SPACING}>
            <Flex gap="lg" flexDirection="column">
              <CommonSummaryInfoSubtitle title="Total Traffic" />
              <TotalTrafficTopSection />
            </Flex>

            <ChartContextProvider
              group={CHART_CONTEXT_GROUPS.BEACON_TOTAL_TRAFFIC_SPLINE.name}
              chartName={CHART_NAMES.BEACON_TOTAL_TRAFFIC_SPLINE_CHART}
            >
              <BeaconComparableSplineChart
                indexName={index}
                fieldName="totalClicks"
                requestBuilder={(builder) => {
                  return removeDspClicks(removeRetailPriceRangeFilters()(builder), !isAdvertisingDisplayMode);
                }}
                beaconChartWithLegendOverride={(props, metric) => {
                  const { formattedPrimaryTotal, formattedPercentChange, primaryTotal, secondaryTotal } = metric;
                  const subtitle = getSubtitle({
                    totalTraffic: formattedPrimaryTotal,
                    formattedPercent: formattedPercentChange,
                    direction: primaryTotal - secondaryTotal
                  });

                  return { ...props, includeSummaryInfo: false, subtitle };
                }}
              />
            </ChartContextProvider>

            <ChartContextProvider
              group={CHART_CONTEXT_GROUPS.BEACON_TOTAL_TRAFFIC_BY_TOPIC.name}
              chartName={CHART_NAMES.BEACON_TOTAL_TRAFFIC_BY_TOPIC_BAR}
            >
              <TrafficByTopicBarChart metric="totalTraffic" />
            </ChartContextProvider>
            <ChartContextProvider
              group={CHART_CONTEXT_GROUPS.BEACON_TOTAL_TRAFFIC_BY_KEYWORD.name}
              chartName={CHART_NAMES.BEACON_TOTAL_TRAFFIC_BY_KEYWORD_BAR}
            >
              {isAdvertisingDisplayMode ? (
                <TrafficByKeywordWithDPV />
              ) : (
                <TrafficAdvancedSearchBarChart metric="totalClicks" groupByField="searchTerm" />
              )}
            </ChartContextProvider>

            <ChartContextProvider
              group={CHART_CONTEXT_GROUPS.BEACON_KEYWORDS_WITH_TRAFFIC.name}
              chartName={CHART_NAMES.BEACON_KEYWORDS_WITH_TRAFFIC_SPLINE}
            >
              <KeywordsWithTrafficSplineChart />
            </ChartContextProvider>
            {!['category', 'subcategory', 'product'].includes(mainEntityType) && (
              <ChartContextProvider
                group={CHART_CONTEXT_GROUPS.BEACON_TOTAL_TRAFFIC_BY_CATEGORY.name}
                chartName={CHART_NAMES.BEACON_TOTAL_TRAFFIC_BY_CATEGORY_BAR}
              >
                <TrafficAdvancedSearchBarChart metric="totalClicks" groupByField="categoryId" />
              </ChartContextProvider>
            )}
            {!['subcategory', 'product'].includes(mainEntityType) && (
              <ChartContextProvider
                group={CHART_CONTEXT_GROUPS.BEACON_TOTAL_TRAFFIC_BY_SUBCATEGORY.name}
                chartName={CHART_NAMES.BEACON_TOTAL_TRAFFIC_BY_SUBCATEGORY_BAR}
              >
                <TrafficAdvancedSearchBarChart metric="totalClicks" groupByField="subCategoryId" />
              </ChartContextProvider>
            )}
            <TotalTrafficGrid />
          </Flex>
        ) : (
          <TrafficNoDataPlaceholder />
        )}
      </BasePageContainer>
    </ExportConfigProvider>
  );
}
